//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import React from 'react';
import styled from 'styled-components';

import { RightsRequestCommunicationType } from '@apis/rights-management/rightsManagementApiModels';
import { PrimaryAnchor } from '@components/atoms/anchor/Anchor';
import { RequiredPermissionsButton } from '@components/atoms/required-permissions-button/RequiredPermissionsButton';
import { CaptionSecondaryText } from '@components/atoms/text/Text';
import { RightsRequestOptions } from '@legacy-components/common/universal-image-actions/constants/universalImageActionsRightsRequestContstants';
import { IRightsOption } from '@legacy-components/rights-management/request-rights-model';
import Cuid from '@models/Cuid';
import { useMediaTrackingContext } from '@tracker/mediaTracking/contexts/MediaTrackingContext';
import { MediaTrackingEventName } from '@tracker/mediaTracking/mediaTrackingModel';
import {
  UIATrackingIds,
  UIA_RIGHTS_REQUEST_PERMISSION,
} from '@uia/constants/uiaConstants';
import { useMediaMetadataQuery } from '@uia/details/hooks/useMediaMetadataQuery';
import {
  MANAGE_RIGHTS_REQUEST_ARTICLE_URL,
  RIGHT_STATUSES_READ_ONLY_FORM,
} from '@uia/rights/constants/uiaRightsConstants';
import { useHasInstagramManageMessagesFeatureAndPermissions } from '@uia/rights/hooks/useHasInstagramManageMessagesFeatureAndPermissions';
import { useHasRightsFormErrorsQuery } from '@uia/rights/hooks/useHasRightsFormErrorsQuery';
import { useInitiateRightsMutation } from '@uia/rights/hooks/useInitiateRightsMutation';
import { useRequestRightsInAuthorizedIgUrl } from '@uia/rights/hooks/useRequestRightsInAuthorizedIgUrl';
import { useRightsConfigurationsQuery } from '@uia/rights/hooks/useRightsConfigurationsQuery';
import { copyTextToClipboard } from '@utils/copyToClipboard';
import { isDefined } from '@utils/filterPredicates';

import { RIGHTS_STATUS } from 'app/bulk-actions/bulk-actions-constants/bulk-actions-constants';

export interface InstagramRequestViaCommentsProps {
  approvalPhrase: string | undefined;
  cuid: Cuid;
  isReRequestRequiredForMissingTCUrl: boolean;
  requestType: RightsRequestOptions;
  rightsRequestText: string | undefined;
  setRightsRequestText: (value: string) => void;
  setSelectedOption: (selectedOption: IRightsOption) => void;
}

export const InstagramRightsRequest = ({
  approvalPhrase,
  cuid,
  isReRequestRequiredForMissingTCUrl,
  requestType,
  rightsRequestText,
  setRightsRequestText,
  setSelectedOption,
}: InstagramRequestViaCommentsProps) => {
  const partialCuid = Cuid.toPartial(cuid);

  const { mutate: initiateRights } = useInitiateRightsMutation();

  const requestRightsInIgUrl = useRequestRightsInAuthorizedIgUrl();

  const { hasInstagramManageMessagesFeatureAndPermissions } =
    useHasInstagramManageMessagesFeatureAndPermissions();

  const {
    data: metadata,
    isError: isErrorMetadata,
    isLoading: isLoadingMetadata,
  } = useMediaMetadataQuery(cuid);

  const {
    data: hasFormErrors,
    isError: isErrorFormErrors,
    isLoading: isLoadingFormErrors,
  } = useHasRightsFormErrorsQuery(rightsRequestText);

  const {
    data: rightsOptions,
    isError: isErrorRightsConfigs,
    isLoading: isLoadingRightsConfigs,
  } = useRightsConfigurationsQuery(Cuid.toPartial(cuid), requestType);

  const { trackEvent } = useMediaTrackingContext();

  if (
    isLoadingMetadata ||
    isErrorMetadata ||
    isLoadingFormErrors ||
    isErrorFormErrors ||
    isLoadingRightsConfigs ||
    isErrorRightsConfigs
  ) {
    return null;
  }

  const {
    networkDMUrl,
    originalUrl,
    rightsRequestStatus: {
      approvalPhrase: previousApprovalPhrase,
      communicationType,
      status,
    },
  } = metadata;

  const getDMRequestMessage = (originalText: string) =>
    !originalText.includes('Post')
      ? `${originalUrl}\n${originalText}`
      : originalText;

  const onClickCopyPaste = async () => {
    if (!rightsRequestText) {
      return;
    }

    trackEvent({
      cuid,
      event: MediaTrackingEventName.RightsRequested,
    });

    const communicationType =
      requestType === RightsRequestOptions.COMMENTS
        ? RightsRequestCommunicationType.COMMENT
        : RightsRequestCommunicationType.DM;

    const requestMessage =
      requestType === RightsRequestOptions.COMMENTS
        ? rightsRequestText
        : getDMRequestMessage(rightsRequestText);

    const targetUrl =
      requestType === RightsRequestOptions.COMMENTS
        ? originalUrl
        : networkDMUrl;

    if (requestMessage !== rightsRequestText) {
      setRightsRequestText(requestMessage);
    }

    await copyTextToClipboard(requestMessage);

    return requestRightsInIgUrl({
      approvalPhrase,
      communicationType,
      itemId: partialCuid,
      requestMessage,
      targetUrl,
    });
  };

  const isReadOnlyForm = RIGHT_STATUSES_READ_ONLY_FORM.includes(status);

  const isCommentsRequest = requestType === RightsRequestOptions.COMMENTS;

  const shouldDisableCopyPasteButton = isCommentsRequest
    ? hasFormErrors
    : hasFormErrors || !hasInstagramManageMessagesFeatureAndPermissions;

  return (
    <Wrapper>
      {status !== RIGHTS_STATUS.EXPIRED ? (
        <>
          {!isReRequestRequiredForMissingTCUrl && !isReadOnlyForm && (
            <FlexStart>
              <RequiredPermissionsButton
                isDisabled={shouldDisableCopyPasteButton}
                onClick={onClickCopyPaste}
                permissionName={UIA_RIGHTS_REQUEST_PERMISSION}
                trackingId={
                  requestType === RightsRequestOptions.COMMENTS
                    ? UIATrackingIds.RIGHTS_REQUEST_COMMENT_REQUEST_BUTTON
                    : UIATrackingIds.RIGHTS_REQUEST_DIRECT_MESSAGE_BUTTON
                }
              >
                Copy and Paste in&nbsp;
                {requestType === RightsRequestOptions.COMMENTS
                  ? 'Instagram'
                  : 'Direct Message'}
              </RequiredPermissionsButton>
              <>
                <CaptionSecondaryText>
                  This will open Instagram in a new window. Make sure you’re
                  logged into the correct Instagram account before posting&nbsp;
                  {requestType === RightsRequestOptions.COMMENTS
                    ? 'the comment'
                    : 'in direct message'}
                  .
                </CaptionSecondaryText>
                <PrimaryAnchor
                  href={MANAGE_RIGHTS_REQUEST_ARTICLE_URL}
                  target='_blank'
                  trackingId={
                    UIATrackingIds.RIGHTS_REQUEST_MANAGE_RIGHTS_LEARN_MORE_LINK
                  }
                >
                  Learn more
                </PrimaryAnchor>
              </>
            </FlexStart>
          )}

          {isReRequestRequiredForMissingTCUrl && (
            <FlexStart>
              <RequiredPermissionsButton
                onClick={() => {
                  const defaultOption = rightsOptions.find(
                    (item) => item.isDefault,
                  );
                  if (isDefined(defaultOption)) {
                    setSelectedOption(defaultOption);
                  }
                }}
                permissionName={UIA_RIGHTS_REQUEST_PERMISSION}
                trackingId={UIATrackingIds.RIGHTS_REQUEST_REQUEST_AGAIN_BUTTON}
              >
                Request Rights Again
              </RequiredPermissionsButton>
              <CaptionSecondaryText>
                Requests are considered Rejected if the owner hasn't responded
                within 3 weeks.
              </CaptionSecondaryText>
            </FlexStart>
          )}
        </>
      ) : (
        <FlexStart>
          <RequiredPermissionsButton
            onClick={() =>
              initiateRights({
                cuid: Cuid.toPartial(cuid),
                request: {
                  approvalPhrase: previousApprovalPhrase,
                  communicationType,
                  requestMessage: rightsRequestText,
                },
              })
            }
            permissionName={UIA_RIGHTS_REQUEST_PERMISSION}
            trackingId={
              UIATrackingIds.RIGHTS_REQUEST_EXTEND_RIGHTS_REQUEST_BUTTON
            }
          >
            Extend Rights Request
          </RequiredPermissionsButton>
          <CaptionSecondaryText>
            This will extend the time the owner of this post has to respond to
            your rights request. Requests are considered Rejected if the owner
            hasn't responded within 3 weeks.
          </CaptionSecondaryText>
        </FlexStart>
      )}
    </Wrapper>
  );
};

const Wrapper = styled.div(
  ({ theme }) => `
    display: flex;
    flex-direction: column;
    gap: ${theme.spacings[2]};
    padding: ${theme.spacings[2]} ${theme.spacings[0]};
  `,
);

const FlexStart = styled.div(
  ({ theme }) => `
    display: flex;
    flex-direction: column;
    justify-content: start;
    align-items: start;
    gap: ${theme.spacings[2]};
  `,
);
